import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, Users, Zap, Brain, TrendingUp, ArrowRight, BarChart3, Bot, GitMerge, Clock, Calendar } from 'lucide-react';
import '../styles/Resources.css';

const Resources = () => {
  // const playbookCategories = [
  //   {
  //     icon: <Brain />,
  //     title: "AI in Business",
  //     description: "Implementation models, use-case frameworks, ROI structures",
  //     count: "12 Playbooks",
  //     color: "electric"
  //   },
  //   {
  //     icon: <TrendingUp />,
  //     title: "GTM Strategies",
  //     description: "Positioning systems, launch mechanics, growth sequencing",
  //     count: "8 Playbooks",
  //     color: "lime"
  //   },
  //   {
  //     icon: <Users />,
  //     title: "Leadership Playbooks",
  //     description: "Decision models, clarity tools, organizational systems",
  //     count: "6 Playbooks",
  //     color: "purple"
  //   },
  //   {
  //     icon: <Zap />,
  //     title: "Productivity & Automation",
  //     description: "Streamlined workflows for teams and leaders",
  //     count: "10 Playbooks",
  //     color: "coral"
  //   },
  //   {
  //     icon: <BookOpen />,
  //     title: "The Kosmoz Perspective",
  //     description: "Observations, Resources, and strategic viewpoints",
  //     count: "15 Articles",
  //     color: "gold"
  //   }
  // ];

  // const formats = [
  //   { name: "Short frameworks", icon: "📋" },
  //   { name: "Cheatsheets", icon: "📄" },
  //   { name: "Templates", icon: "📑" },
  //   { name: "Automation guides", icon: "⚡" },
  //   { name: "Leadership notes", icon: "📝" }
  // ];

  const featuredResources = [
    {
      title: "Reporting vs. Business Intelligence vs. Analytics",
      subtitle: "Which One Does Your Business Need?",
      category: "Business Intelligence",
      author: "Abhishek Nishis",
      date: "Dec 15, 2024",
      readTime: "8 min read",
      preview: "While reporting focuses on structured, predefined data presentation, BI offers interactive insights, and analytics goes a step further with AI-driven forecasting and decision-making.",
      link: "/blog/reporting-vs-bi-vs-analytics",
      icon: <BarChart3 size={24} />,
      badge: "BI Strategy"
    },
    {
      title: "The Power of Generative AI and Agentic AI",
      subtitle: "From Automation to Intelligent Decision-Making",
      category: "Artificial Intelligence",
      author: "Abhishek Nishis",
      date: "Dec 20, 2024",
      readTime: "10 min read",
      preview: "Generative AI can create content—text, images, code, and even music. Agentic AI can act autonomously—making decisions, solving problems, and executing multi-step processes.",
      link: "/blog/generative-ai-vs-agentic-ai",
      icon: <Bot size={24} />,
      badge: "AI Innovation"
    },
    {
      title: "How to Plan BI Consolidation?",
      subtitle: "Can a single BI product fulfill all enterprise needs?",
      category: "BI Strategy",
      author: "Abhishek Nishis",
      date: "Dec 10, 2024",
      readTime: "7 min read",
      preview: "Navigating the complex landscape of BI consolidation—exploring pros and cons, key considerations, and whether a hybrid approach might be the best solution for your enterprise.",
      link: "/blog/bi-consolidation",
      icon: <GitMerge size={24} />,
      badge: "Enterprise Strategy"
    }
  ];

  return (
    <div className="Resources-page">

      {/* HERO */}
      <section className="Resources-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="Resources-hero-content"
          >
            <h1 className="Resources-hero-title">Resources</h1>
            <p className="Resources-hero-subtitle">Practical, accessible frameworks for leaders</p>
          </motion.div>
        </div>
      </section>


      {/* FEATURED Resources */}
      <section className="section featured-section">
        <div className="container">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Featured Resources</h2>
            <p className="section-subtitle">Recent frameworks and strategic perspectives</p>
          </motion.div>

          <div className="featured-grid">
            {featuredResources.map((resource, index) => (
              <Link 
                to={resource.link} 
                key={resource.title}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="k-card"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="card-icon-wrapper">
                    {resource.icon}
                  </div>

                  <div className="badge">{resource.badge}</div>

                  <h3 className="k-card-title">{resource.title}</h3>
                  {resource.subtitle && (
                    <h4 className="k-card-subtitle">{resource.subtitle}</h4>
                  )}

                  <p className="k-card-text">
                    {resource.preview}
                  </p>

                  <div className="k-card-meta">
                    <span>{resource.author}</span> • <span>{resource.date}</span> • <span>{resource.readTime}</span>
                  </div>
                  
                  <div className="read-more-link">
                    Read Article <ArrowRight size={16} />
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* TOOLKIT PREVIEW */}
      {/* <section id="toolkit" className="section toolkit-preview-section">
        <div className="container">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="toolkit-preview"
          >
            <h2 className="section-title">Coming Soon: Toolkit</h2>
            <p className="section-subtitle">
              A collection of structured, practical resources for teams building with speed and clarity
            </p>

            <div className="toolkit-items">
              <div className="toolkit-item">AI Strategy Templates</div>
              <div className="toolkit-item">GTM Launch Kits</div>
              <div className="toolkit-item">Webflow Templates</div>
              <div className="toolkit-item">Automation Packs</div>
              <div className="toolkit-item">Kosmoz Lab Experiments</div>
            </div>

            <p className="toolkit-text">
              Built for teams that prefer to move quickly and independently.
            </p>
          </motion.div>

        </div>
      </section> */}

    </div>
  );
};

export default Resources;